import "../../styles/send-alert-embed.scss";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { dispatch } from "../../redux/redux-config";
import {
  AlertType,
  Path,
  SmsCharLimit,
  SmsCreditTiers,
  MAX_TTS_DURATION_SECONDS,
  TTS_CHAR_LIMIT,
  TTS_CHAR_WARNING_LIMIT,
  TTS_TRIM_TARGET_SECONDS,
} from "../../utilities/constants";
import {
  formatGridDate,
  useUpdateState,
  getAudioDurationSeconds,
  estimateTtsDurationSecondsCalibrated,
  formatDurationShort,
  trimTtsTextToDuration,
} from "../../utilities/utilities";
import GuiHtmlEditor from "../../components/global-ui/gui-html-editor";
import * as DOMPurify from "dompurify";
import { GuiTextbox } from "../../components/global-ui/gui-textbox";
import { GuiTextArea } from "../../components/global-ui/gui-textarea";
import { VoiceRecorder } from "../../components/voice-recorder";
import ValidationContext, {
  useValidation,
} from "../../components/global-ui/validation";
import Error from "../../components/global-ui/error";
import { TabItem } from "../../components/tab-item";
import { GuiButton } from "../../components/global-ui/gui-button";
import { ConfirmCloseModal } from "../../components/confirm-close-modal";
import { ConfirmDeleteModal } from "../../components/confirm-delete-modal";
import { FamilyPicker } from "../../components/family-picker";
import { FamilyWorkgroupPicker } from "../../components/family-workgroup-picker";
import { MemberPicker } from "../../components/member-picker";
import { MemberWorkgroupPicker } from "../../components/member-workgroup-picker";
import LoadingSpinner, { Spinner } from "../../components/loading-spinner";
import ScheduleAlertModal from "../../components/schedule-alert-modal";
import AudioPlayer from "../../components/audio-player";
import actions from "../../actions/actions";
import {
  useNavState,
  pushNavState,
  popNavState,
} from "../../utilities/navigation";
import navigate from "../../utilities/navigation";
import ExpandPanel from "../../components/expand-panel";
import { GuiSelect } from "../../components/global-ui/gui-select";
import { showToast } from "../../components/toast";

const SMS_CHAR_LIMIT = SmsCharLimit;

// Fixed spam patterns - more reliable word matching
var spamPatterns = [
  /(?:^|\s)(fraud|fruad|recent transaction|recent transection|Bank of Amercia|Bank of Ameria|Bank of Americ|Bnk of America|Bank of Amerca|Bank of Americaa|Click here to verify|Click here for verify|Access your account now|Payment declined|transaction declined|transection declined|Verify payment method|Verify your payment method|click to see important message|click here to see important message)(?:\s|$|[.,!?])/gi,
];

var highRiskKeywords = [/\bBank of America\b/gi];

// Whitelist for legitimate domains that should never be flagged
var legitimateDomains = [
  /microsoft\.com/i,
  /microsoft\.co/i,
  /teams\.microsoft/i,
  /office\.com/i,
  /sharepoint\.com/i,
  /\bbit\.ly\b/i,
  /\btinyurl\.com\b/i,
];

// More refined spam domains - separate legitimate shorteners from suspicious ones
var suspiciousShorteners = [/\bt\.co\b/i, /\bgoo\.gl\b/i, /\bow\.ly\b/i];

const UNIVERSAL_SPAM_WARNING =
  "Warning: This account is not enabled for promotional messaging. Please contact support for assistance.";

// Enhanced URL detection function
function detectAndValidateUrls(text) {
  var urlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/gi;
  var urls = text.match(urlRegex) || [];
  var urlAnalysis = {
    totalUrls: urls.length,
    spamUrls: [],
    legitUrls: [],
    suspiciousUrls: [],
    spamScore: 0,
  };

  if (urls.length === 0) {
    return urlAnalysis;
  }

  urls.forEach(function (url) {
    var urlScore = 0;
    var urlStatus = "unknown";

    // Check if URL is from a legitimate domain first
    var isLegitimate = legitimateDomains.some(function (pattern) {
      return pattern.test(url);
    });

    if (isLegitimate) {
      urlAnalysis.legitUrls.push(url);
      return; // Skip further checks for whitelisted domains
    }

    // Check for suspicious shorteners (separate from legitimate ones)
    var isSuspiciousShortener = suspiciousShorteners.some(function (pattern) {
      return pattern.test(url);
    });

    if (isSuspiciousShortener) {
      urlScore += 15; // Lower score for suspicious shorteners
      urlStatus = "suspicious";
    }

    // Check for phishing indicators in URL
    if (hasPhishingIndicators(url)) {
      urlScore += 40;
      urlStatus = "spam";
    }

    // Categorize URL based on score
    if (urlScore >= 50 || urlStatus === "spam") {
      urlAnalysis.spamUrls.push(url);
      urlAnalysis.spamScore += urlScore;
    } else if (urlScore >= 15 || urlStatus === "suspicious") {
      urlAnalysis.suspiciousUrls.push(url);
      urlAnalysis.spamScore += urlScore * 0.3; // Reduced impact of suspicious URLs
    } else {
      urlAnalysis.legitUrls.push(url);
    }
  });

  return urlAnalysis;
}

// Helper function to detect phishing indicators
function hasPhishingIndicators(url) {
  var phishingPatterns = [
    // Common phishing domain typos
    /bankofamer[1i]ca/i,
    /we11sfargo/i,
    /paypa1/i,
    /app1e/i,
    /m1crosoft/i,
    /arnazon/i,
    // Homograph attacks (similar looking characters)
    /аpple/i, // Cyrillic 'а' instead of 'a'
    /miсrosoft/i, // Cyrillic 'с' instead of 'c'
  ];
  return phishingPatterns.some(function (pattern) {
    return pattern.test(url);
  });
}

const toggleSendButtons = (show) => show;

// Fixed detectSpamPatterns function with proper state management
function detectSpamPatterns(text) {
  var spamScore = 0;

  // Input validation
  if (!text || typeof text !== "string") {
    toggleSendButtons(true);
    return { score: 0, buttonShow: true };
  }

  if (text.length < 10) {
    toggleSendButtons(true);
    return { score: 0, buttonShow: true };
  }

  // Reset all regex patterns to avoid state issues
  spamPatterns.forEach(function (pattern) {
    pattern.lastIndex = 0;
  });

  highRiskKeywords.forEach(function (pattern) {
    pattern.lastIndex = 0;
  });

  // Check for high-risk keywords first - immediate block
  var highRiskDetected = false;
  for (var i = 0; i < highRiskKeywords.length; i++) {
    highRiskKeywords[i].lastIndex = 0; // Reset before each test

    if (highRiskKeywords[i].test(text)) {
      highRiskDetected = true;
      spamScore = 100;
      break; // Exit immediately on first match
    }
  }

  if (highRiskDetected) {
    toggleSendButtons(false);
    return {
      score: spamScore,
      buttonShow: false,
    };
  }

  // Continue with other checks only if no high-risk detected
  var urlAnalysis = detectAndValidateUrls(text);

  if (urlAnalysis.spamUrls.length > 0) {
    spamScore += 50;
  }

  if (urlAnalysis.suspiciousUrls.length > 0) {
    spamScore += 15;
  }

  if (urlAnalysis.totalUrls > 3) {
    spamScore += 20;
  }

  spamScore += urlAnalysis.spamScore;

  // FIXED: Check spam word patterns with better logging
  spamPatterns[0].lastIndex = 0; // Reset before use
  var matches = text.match(spamPatterns[0]) || [];
  var spamWordMatches = matches.length;

  // CHANGED: Trigger warning on 1 or more spam words (was > 1, now >= 1)
  if (spamWordMatches > 0) {
    spamScore += spamWordMatches * 8; // Each additional word adds more score
  }

  var buttonShow = spamScore > 0 ? false : true;
  toggleSendButtons(buttonShow);

  return {
    score: spamScore,
    buttonShow: buttonShow,
  };
}

function isMultiType(alert) {
  const types = [alert?.includeText, alert?.includeEmail, alert?.includeVoice];
  return types.filter(Boolean).length > 1;
}

export default function SendAlertEmbed(props) {
  const [requestResponse, setRequestResponse] = useState("0");
  const [requestEmailResponse, setRequestEmailResponse] = useState("");
  const [emailDataSender, setPSEmailDataSender] = useState([]);
  const [spamResults, setSpamResults] = useState({
    text: { warnings: [], score: 0, buttonShow: true },
    voice: { warnings: [], score: 0, buttonShow: true },
    email: { warnings: [], score: 0, buttonShow: true },
  });

  let alert = useSelector((store) => store?.alert?.current || {});
  let { step = 0, saving = false } = useSelector((store) => store.alert || {});
  const [uiStep, setUiStep] = useState(step);
  useEffect(() => {
    setUiStep(step);
  }, [step]);
  let isMobile = useSelector((store) => store.page?.isMobile || false);
  let {
    pickGroups,
    pickMembers,
    pickFamilyWorkgroups,
    pickMemberWorkgroups,
    confirmClose,
    confirmSave,
    confirmDiscard,
  } = useNavState((loc) => loc);

  let initialState = useMemo(
    () => ({
      voiceTab:
        alert?.voiceFileIdentifier && alert?.t2sUrl ? "texttospeech" : "record",
      lastTTSMessage: alert?.voiceFileIdentifier ? alert?.t2sMessage : null,
      savingFamilyMembers: false,
    }),
    [alert?.voiceFileIdentifier, alert?.t2sUrl, alert?.t2sMessage],
  );
  let [state, updateState] = useUpdateState(initialState);

  const detectSpamPatternsCallback = useCallback((text, type = "text") => {
    const normalizedType =
      type === "voice" ? "voice" : type === "email" ? "email" : "text";
    const detectionResult = detectSpamPatterns(text);
    const enrichedResult = {
      warnings: detectionResult.buttonShow ? [] : [UNIVERSAL_SPAM_WARNING],
      score: detectionResult.score,
      buttonShow: detectionResult.buttonShow,
    };

    setSpamResults((prev) => ({
      ...prev,
      [normalizedType]: enrichedResult,
    }));

    return enrichedResult;
  }, []);

  const hasSpamContent = useMemo(
    () =>
      Object.values(spamResults).some(
        (result) => result && result.buttonShow === false,
      ),
    [spamResults],
  );

  const renderSpamWarnings = (type) => {
    const result = spamResults[type];
    if (!result || result.buttonShow !== false) return null;

    return (
      <div className="spam-warning-container">
        <div className="spam-warning-message">{UNIVERSAL_SPAM_WARNING}</div>
      </div>
    );
  };

  // Escalation (redux-based)
  const escalation = useSelector((s) => s.escalation);

  // Memoize sanitized email HTML for preview performance
  const sanitizedEmailHTML = useMemo(
    () => (alert?.emailMessage ? DOMPurify.sanitize(alert.emailMessage) : ""),
    [alert?.emailMessage],
  );

  // Notices for recipients missing a home phone (Text) or email address (Email)
  const recipientContactNotices = useMemo(() => {
    const groups = Array.isArray(alert?.groups) ? alert.groups : [];
    const notices = [];

    // Treats null/undefined, blank strings and "undefined"/"null" text as empty
    const isEmpty = (v) => {
      if (v === null || v === undefined) return true;
      const str = String(v).trim().toLowerCase();
      return str === "" || str === "undefined" || str === "null";
    };

    const firstValue = (g, keys) =>
      keys.map((k) => g?.[k]).find((v) => !isEmpty(v));

    const countMissing = (type, keys) =>
      groups.filter(
        (g) =>
          !g?.is_temp &&
          g?._pickerType === type &&
          isEmpty(firstValue(g, keys)),
      ).length;

    const label = (count, singular, plural) =>
      `${count} ${count === 1 ? singular : plural}`;

    if (alert?.includeText) {
      const familyPhoneKeys = ["familyHomePhone"];

      const memberPhoneKeys = ["homePhone", "mobilePhone"];
      const families = countMissing("family", familyPhoneKeys);
      const members = countMissing("member", memberPhoneKeys);
      if (families > 0)
        notices.push(
          `${label(families, "family", "families")} does not contain a phone number.`,
        );
      if (members > 0)
        notices.push(
          `${label(members, "member", "members")} does not contain a phone number.`,
        );
    }

    if (alert?.includeEmail) {
      const emailKeys = [
        "emailAddress",
        "email",
        "eMailAddress",
        "familyEmail",
      ];
      const families = countMissing("family", emailKeys);
      const members = countMissing("member", emailKeys);
      if (families > 0)
        notices.push(
          `${label(families, "family", "families")} does not contain an email address.`,
        );
      if (members > 0)
        notices.push(
          `${label(members, "member", "members")} does not contain an email address.`,
        );
    }

    return notices;
  }, [alert?.groups, alert?.includeText, alert?.includeEmail]);

  let validation = useValidation(validate, [alert, state]);
  let howTabValid = !validation.getError("tab0");
  let whoTabValid = !validation.getError("tab1");
  let messageTabValid = !validation.getError("tab2");

  // Load Reply-To email + Sender Name options on mount
  const loadPSEmailAndSenderNames = async () => {
    const result = await actions.parishSoftApis.loadPSEmailAndSenderNames();
    setPSEmailDataSender(result);
  };
  useEffect(() => {
    loadPSEmailAndSenderNames();
  }, []);

  // Keep Reply-To selection in sync when editing an existing alert.
  useEffect(() => {
    setRequestEmailResponse(alert?.replyTo ? alert.replyTo.toString() : "");
  }, [alert?.id, alert?.replyTo]);

  // Auto-set default replyTo + emailSenderName from first row once data loads
  useEffect(() => {
    if (emailDataSender.length > 0 && !alert?.replyTo) {
      const first = emailDataSender[0];
      setRequestEmailResponse(first.email?.toString() || "");
      dispatch("UPDATE_NEW_ALERT", {
        ...alert,
        emailSenderName: first.senderName || "",
      });
    }
  }, [emailDataSender]);

  // moved earlier; removing duplicate definition

  useEffect(() => {
    if (alert?.alertType === AlertType.Text) {
      dispatch("UPDATE_NEW_ALERT", {
        ...alert,
        includeText: true,
        smsMessage: alert?.message,
      });
    }
    if (alert?.alertType === AlertType.Email) {
      dispatch("UPDATE_NEW_ALERT", {
        ...alert,
        includeEmail: true,
        emailMessage: alert?.message,
      });
    }
    if (alert?.alertType === AlertType.Voice) {
      dispatch("UPDATE_NEW_ALERT", {
        ...alert,
        includeVoice: true,
        t2sMessage: alert?.message,
        t2sUrl: alert?.voiceFileUrl,
      });
    }
  }, [alert]);

  useEffect(() => {
    if (alert?.includeText || alert?.includeVoice) {
      detectSpamPatternsCallback(alert?.smsMessage || "", "text");
    } else {
      detectSpamPatternsCallback("", "text");
    }
  }, [
    alert?.includeText,
    alert?.includeVoice,
    alert?.smsMessage,
    detectSpamPatternsCallback,
  ]);

  useEffect(() => {
    if (alert?.includeVoice && state.voiceTab === "texttospeech") {
      detectSpamPatternsCallback(alert?.t2sMessage || "", "voice");
    } else {
      detectSpamPatternsCallback("", "voice");
    }
  }, [
    alert?.includeVoice,
    alert?.t2sMessage,
    state.voiceTab,
    detectSpamPatternsCallback,
  ]);

  // Email spam detection (strip HTML to analyze text content)
  useEffect(() => {
    if (alert?.includeEmail) {
      const raw =
        typeof alert?.emailMessage === "string" ? alert.emailMessage : "";
      const emailText = raw
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/\s+/g, " ")
        .trim();
      detectSpamPatternsCallback(emailText || "", "email");
    } else {
      detectSpamPatternsCallback("", "email");
    }
  }, [alert?.includeEmail, alert?.emailMessage, detectSpamPatternsCallback]);

  const toggleType = useCallback(
    (type) => {
      const nextAlert = {
        ...alert,
        [type]: !alert[type],
      };

      // If switching to multi-type and Text is selected, sync email fields
      const types = [
        nextAlert.includeText,
        nextAlert.includeEmail,
        nextAlert.includeVoice,
      ];
      if (
        types.filter(Boolean).length > 1 &&
        nextAlert.includeText &&
        nextAlert.includeEmail
      ) {
        // Only update if emailMessage is empty or different
        if (
          !nextAlert.emailMessage ||
          nextAlert.emailMessage !== nextAlert.smsMessage
        ) {
          nextAlert.emailMessage = nextAlert.smsMessage || "";
        }
        // Sync subject if needed
        nextAlert.emailSubject = nextAlert.name;
      }

      dispatch("UPDATE_NEW_ALERT", nextAlert);
      updateState({
        previewed: false,
        ttsTrim: null,
        ttsDurationSeconds: null,
        ttsCharsPerSecond: null,
        ttsBannerDismissed: false,
      });
    },
    [alert, updateState],
  );

  const update = useCallback(
    (name, value) => {
      dispatch("UPDATE_NEW_ALERT", { ...alert, [name]: value });
      updateState({ previewed: false });
    },
    [alert, updateState],
  );

  // Shared SMS handlers (single and multi-type)
  const handleSmsKeyDown = useCallback(
    (e) => {
      if (
        alert?.smsMessage &&
        alert?.smsMessage.length >= SMS_CHAR_LIMIT &&
        !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(
          e.key,
        )
      ) {
        e.preventDefault();
      }
    },
    [alert?.smsMessage],
  );

  const handleSmsPasteCapped = useCallback(
    (e) => {
      const paste = (e.clipboardData || window.clipboardData).getData("text");
      const newValue = (alert?.smsMessage || "") + paste;
      if (newValue.length > SMS_CHAR_LIMIT) {
        e.preventDefault();
        dispatch("UPDATE_NEW_ALERT", {
          ...alert,
          smsMessage: newValue.slice(0, SMS_CHAR_LIMIT),
          t2sUrl: "",
          voiceFileIdentifier: "",
        });
        updateState({
          previewed: false,
          ttsTrim: null,
          ttsDurationSeconds: null,
          ttsCharsPerSecond: null,
          ttsBannerDismissed: false,
        });
      }
    },
    [alert, updateState],
  );

  const handleSmsChangeSingle = useCallback(
    (name, value) => {
      if (value.length > SMS_CHAR_LIMIT) value = value.slice(0, SMS_CHAR_LIMIT);
      update(name, value);
    },
    [update],
  );

  const handleSmsChangeMulti = useCallback(
    (name, value) => {
      if (value.length > SMS_CHAR_LIMIT) {
        value = value.slice(0, SMS_CHAR_LIMIT);
      }
      dispatch("UPDATE_NEW_ALERT", {
        ...alert,
        smsMessage: value,
        emailMessage: value,
        name: alert.name,
        emailSubject: alert.name,
        t2sMessage: value,
        t2sUrl: "",
        voiceFileIdentifier: "",
      });
      updateState({
        previewed: false,
        ttsTrim: null,
        ttsDurationSeconds: null,
        ttsCharsPerSecond: null,
        ttsBannerDismissed: false,
      });
    },
    [alert, updateState],
  );

  const saveFamilyMemberDetails = useCallback(async () => {
    const selectedRecipients = Array.isArray(alert.groups) ? alert.groups : [];

    if (selectedRecipients.length === 0) {
      dispatch("UPDATE_NEW_ALERT", { ...alert, hgsGroups: [] });
      return true;
    }

    updateState({ savingFamilyMembers: true });
    try {
      const result =
        await actions.parishSoftApis.saveFamilyMemberDetails(
          selectedRecipients,
        );

      if (result?.success) {
        dispatch("UPDATE_NEW_ALERT", {
          ...alert,
          hgsGroups: Array.isArray(result?.groups) ? result.groups : [],
        });
        return true;
      }

      console.error(
        "Failed to save selected recipients:",
        result?.errorMessage || "Unknown error",
      );
      return false;
    } catch (error) {
      console.error("Failed to save selected recipients:", error);
      return false;
    } finally {
      updateState({ savingFamilyMembers: false });
    }
  }, [alert, updateState]);

  const changeStep = useCallback(
    async (step) => {
      if (step > 0 && !howTabValid) {
        return;
      }
      if (step > 1 && !whoTabValid) {
        return;
      }
      if (step > 2 && !messageTabValid) {
        return;
      }

      // If moving to step 2 (Create Message), call saveFamilyMemberDetails API
      if (step === 2 && uiStep === 1) {
        const saved = await saveFamilyMemberDetails();
        if (!saved) {
          return;
        }
      }

      // If moving to step 3 (Preview and Send), auto-convert TTS if needed
      if (step === 3) {
        if (
          isMultiType(alert) &&
          alert?.smsMessage &&
          (alert?.t2sMessage !== alert?.smsMessage || !alert?.t2sUrl)
        ) {
          updateState({ convertingTTS: true, ttsDurationSeconds: null });
          const result = await actions.parishSoftApis.textToSpeech(
            alert?.smsMessage,
          );
          const durationSeconds = result?.success
            ? await getAudioDurationSeconds(result?.fileUrl)
            : null;
          updateState({
            convertingTTS: false,
            lastTTSMessage: alert?.smsMessage,
            ttsDurationSeconds: durationSeconds,
          });
          if (result.success) {
            dispatch("UPDATE_NEW_ALERT", {
              ...alert,
              t2sMessage: alert?.smsMessage,
              t2sUrl: result?.fileUrl,
              voiceFileIdentifier: result?.fileIdentifier,
            });
          }
          if (durationSeconds > MAX_TTS_DURATION_SECONDS) return;
        }
      }

      setUiStep(step);
      if (step === 3) updateState({ previewed: true });
    },
    [
      howTabValid,
      whoTabValid,
      messageTabValid,
      alert,
      updateState,
      uiStep,
      saveFamilyMemberDetails,
    ],
  );

  const onHowPanelHeaderClick = useCallback(() => {
    if (uiStep === 0) {
      // no-op: retained for future analytics
    }
    changeStep(1);
  }, [uiStep, changeStep]);

  function discardAlert() {
    navigate(Path.EmbedMessages);
  }

  function showPopup(popup) {
    pushNavState({ [popup]: true });
  }

  function hidePopup() {
    popNavState();
  }

  function groupsChanged(selected) {
    // Merge new selections with existing groups, avoiding duplicates by ID
    const existingIds = new Set(alert.groups?.map((g) => g.id) || []);
    const mergedGroups = [
      ...(alert.groups || []),
      ...selected.filter((s) => !existingIds.has(s.id)),
    ];

    dispatch("UPDATE_NEW_ALERT", {
      ...alert,
      groups: mergedGroups,
      hgsGroups: [],
    });
    const updates = { previewed: false };
    if (mergedGroups.length === 0) updates.activePickerType = null;
    updateState(updates);
    hidePopup();
  }

  function removeGroup(group) {
    const newGroups = alert.groups.filter((g) => g !== group);
    dispatch("UPDATE_NEW_ALERT", {
      ...alert,
      groups: newGroups,
      hgsGroups: [],
    });
    const updates = { previewed: false };
    if (newGroups.length === 0) updates.activePickerType = null;
    updateState(updates);
  }

  async function convertTTS(textOverride) {
    // textOverride is only ever a string when called from a trim action; button
    // onClick handlers pass a DOM event, so guard against treating that as text.
    const overrideText = typeof textOverride === "string" ? textOverride : null;
    const textToConvert = overrideText || alert?.t2sMessage;
    if (!state?.convertingTTS && textToConvert) {
      updateState({ convertingTTS: true, ttsDurationSeconds: null });
      var result = await actions.parishSoftApis.textToSpeech(textToConvert);
      const durationSeconds = result?.success
        ? await getAudioDurationSeconds(result?.fileUrl)
        : null;
      // Calibrate the estimator to the rate actually observed for this conversion,
      // so the live counter stops drifting from a generic per-language guess.
      const charsPerSecond =
        durationSeconds > 0 ? textToConvert.length / durationSeconds : null;
      updateState({
        convertingTTS: false,
        lastTTSMessage: alert?.t2sMessage,
        ttsDurationSeconds: durationSeconds,
        ttsCharsPerSecond: charsPerSecond,
        ttsBannerDismissed: false,
      });

      if (result?.success) {
        dispatch("UPDATE_NEW_ALERT", {
          ...alert,
          t2sUrl: result?.fileUrl,
          voiceFileIdentifier: result?.fileIdentifier, // Add this line
        });
      }
    }
  }

  // Never truncates or rate-adjusts audio — only re-converts a shorter, sentence-aligned
  // portion of the source text. The dropped remainder is kept (not deleted) for display.
  function applyTtsTrim(targetSeconds) {
    const { keptText, overflowText, trimmed } = trimTtsTextToDuration(
      alert?.t2sMessage,
      state?.ttsDurationSeconds,
      targetSeconds,
    );
    if (!trimmed || !keptText) return;
    updateState({ ttsTrim: { keptText, overflowText } });
    convertTTS(keptText);
  }

  function handleShortenToFit() {
    // Small safety margin so the re-converted audio reliably lands under the hard limit.
    applyTtsTrim(MAX_TTS_DURATION_SECONDS - 5);
  }

  function handleTrimTo105() {
    applyTtsTrim(TTS_TRIM_TARGET_SECONDS);
  }

  function dismissTtsDurationBanner() {
    updateState({ ttsBannerDismissed: true });
  }

  function handleTtsMessageChange(name, value) {
    dispatch("UPDATE_NEW_ALERT", {
      ...alert,
      [name]: (value || "").slice(0, TTS_CHAR_LIMIT),
    });
    updateState({ previewed: false, ttsTrim: null, ttsBannerDismissed: false });
  }

  function updateVoiceRecording(name, value) {
    dispatch("UPDATE_NEW_ALERT", {
      ...alert,
      [name]: value,
      voiceFileIdentifier: null,
      voiceFileUrl: null,
    });
    updateState({ previewed: false });
  }

  function onAttachmentAdded(name, info) {
    dispatch("UPDATE_NEW_ALERT", {
      ...alert,
      attachments: [...alert.attachments, info],
    });
    updateState({ previewed: false });
  }

  function removeAttachment(attachment) {
    dispatch("REMOVE_ATTACHMENT", attachment);
    updateState({ previewed: false });
  }

  function scheduleClick() {
    if (!validation.allValid()) return validation.setShow(true);

    showPopup("confirmSave");
  }

  function saveSchedule(date) {
    hidePopup();
    save(false, date);
  }

  async function save(isDraft, scheduledDate) {
    isDraft = isDraft === true;

    if (!isDraft && !validation.allValid()) return validation.setShow(true);

    let newAlert = {
      id: alert.id,
      alertName: alert.name,
      scheduledDate: scheduledDate,
      allGroups: alert.allGroups,
      groups: (alert.hgsGroups?.length ? alert.hgsGroups : alert.groups).map(
        (g) => g.id,
      ),
      recipientType:
        alert.groups?.[0]?._pickerType || state.activePickerType || null,
      priorityLevel: alert.isEmergency ? 10 : 0,
      callLevel:
        escalation.mode === "specify" && escalation.selectedLevel
          ? parseInt(escalation.selectedLevel, 10) || 0
          : 0,
      isDraft: isDraft,
    };

    if (alert.includeText) {
      newAlert.isSms = true;
      newAlert.smsMessage = alert.smsMessage;
      // Add Alert2WaySms flag based on dropdown
      newAlert.Alert2WaySms = requestResponse === "1" ? true : false;
    }

    if (alert.includeEmail) {
      newAlert.isEmail = true;
      const body = (alert.emailMessage || "").trim();
      let isHtmlBody = /<\/?[a-z][\w-]*[^>]*>/i.test(body);
      if (isHtmlBody) {
        const m = body.match(/^<p[^>]*>\s*([\s\S]*?)\s*<\/p>$/i);
        if (m) {
          const inner = m[1].trim();
          if (!/<\/?[a-z][\w-]*[^>]*>/i.test(inner)) {
            isHtmlBody = false;
          }
        }
      }

      let emailBodyToSend = body;
      if (!isHtmlBody) {
        // Strip a single paragraph wrapper for plain text
        const m = body.match(/^<p[^>]*>\s*([\s\S]*?)\s*<\/p>$/i);
        emailBodyToSend = m ? m[1] : body;
      }

      newAlert.emailMessage = emailBodyToSend;
      if (isHtmlBody) newAlert.htmlMessage = emailBodyToSend;
      newAlert.emailSubject = alert.emailSubject || alert.name || "";
      // Backend flag: 1 = plain text, 0 = HTML
      newAlert.email_text_html = isHtmlBody ? 0 : 1;
      newAlert.attachments = alert.attachments;
      newAlert.replyTo = requestEmailResponse || "";
      newAlert.emailSenderName = alert.emailSenderName || "";
    }

    if (alert.includeVoice) {
      newAlert.isVoice = true;
      // Always bind TTS message and voice file URL
      newAlert.ttsMessage = alert.t2sMessage || alert.smsMessage || "";
      newAlert.voiceFileUrl = alert.t2sUrl || "";
      if (alert.voiceFileIdentifier) {
        newAlert.voiceFileIdentifier = alert.voiceFileIdentifier;
      }
      if (state.voiceTab === "record") {
        newAlert.voiceRecording = alert.voiceRecording;
      }
    }
    actions.parishSoftApis.saveAlert(newAlert);
  }

  // isMultiType moved to module scope

  function ListItem({ children, onRemove }) {
    return (
      <span>
        {children}
        <i className="fas fa-times" onClick={onRemove}></i>
      </span>
    );
  }

  function getSmsCreditCount(charCount) {
    if (charCount <= 160) return 1;
    if (charCount <= 306) return 2;
    if (charCount <= 460) return 3;
    return 4;
  }

  function SmsCreditsIndicator({ charCount }) {
    const count = charCount || 0;
    const creditCount = getSmsCreditCount(count);
    const progressPercent = Math.min((count / SMS_CHAR_LIMIT) * 100, 100);

    return (
      <div className="sms-credits-widget">
        <div className="sms-char-count-row">
          <span className="sms-char-count">
            {count}/{SMS_CHAR_LIMIT} characters
          </span>
          <div className="sms-credit-badge">
            <span>
              {creditCount} {creditCount === 1 ? "message" : "messages"}
            </span>
            <div className="sms-credit-tooltip-wrapper" tabIndex={0}>
              <i
                className="fa fa-info-circle sms-credit-info-icon"
                aria-hidden="true"
              ></i>
              <div className="sms-credit-tooltip">
                <div className="sms-credit-tooltip-title">Messages</div>
                <div className="sms-credit-tooltip-subtitle">
                  Message usage is based on message length:
                </div>
                {SmsCreditTiers.map((tier) => (
                  <div className="sms-credit-tooltip-row" key={tier.range}>
                    <span>{tier.range.replace("chars", "characters")}</span>
                    <span>{tier.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="sms-credits-indicator">
          <div className="sms-credits-progress-track">
            <div
              className="sms-credits-progress-fill"
              style={{ width: `${progressPercent}%` }}
            ></div>
            <span className="sms-credits-progress-divider sms-credits-progress-divider-1"></span>
            <span className="sms-credits-progress-divider sms-credits-progress-divider-2"></span>
            <span className="sms-credits-progress-divider sms-credits-progress-divider-3"></span>
          </div>
        </div>
      </div>
    );
  }

  function TtsCharCounter({ charCount }) {
    const count = charCount || 0;
    const nearLimit = count >= TTS_CHAR_WARNING_LIMIT;
    const estimatedSeconds = estimateTtsDurationSecondsCalibrated(
      count,
      state?.ttsCharsPerSecond,
    );

    return (
      <div className="tts-char-counter">
        <div className="tts-char-count-row">
          <span className={"tts-char-count" + (nearLimit ? " warning" : "")}>
            {count}/{TTS_CHAR_LIMIT} characters
          </span>
          <span className="tts-estimated-duration">
            Est. length: {formatDurationShort(estimatedSeconds)}
          </span>
        </div>
        {nearLimit && (
          <div className="tts-char-warning">
            Approaching the {TTS_CHAR_LIMIT}-character limit. Longer messages
            take longer to convert and are more likely to exceed the 2 minute
            recording limit.
          </div>
        )}
      </div>
    );
  }

  function TtsDurationBanner() {
    const durationSeconds = state?.ttsDurationSeconds || 0;
    const overageSeconds = Math.max(
      0,
      Math.round(durationSeconds - MAX_TTS_DURATION_SECONDS),
    );

    return (
      <div className="tts-duration-banner">
        <div className="tts-duration-banner-message">
          This recording is {formatDurationShort(durationSeconds)} —{" "}
          {overageSeconds} second{overageSeconds === 1 ? "" : "s"} over the 2
          minute limit.
        </div>
        <div className="tts-duration-banner-actions">
          <GuiButton
            dataQa="tts-shorten-to-fit"
            className="tts-shorten-to-fit"
            disabled={state?.convertingTTS}
            onClick={handleShortenToFit}
          >
            Shorten to fit
          </GuiButton>
          <GuiButton
            dataQa="tts-trim-105"
            className="btn-outline"
            disabled={state?.convertingTTS}
            onClick={handleTrimTo105}
          >
            Trim to 1:45
          </GuiButton>
          <GuiButton
            dataQa="tts-edit-message"
            className="btn-outline"
            onClick={dismissTtsDurationBanner}
          >
            Edit message
          </GuiButton>
        </div>
      </div>
    );
  }

  function validate([alert, state]) {
    let errors = {};

    // Alert Type tab
    if (!alert.includeText && !alert.includeEmail && !alert.includeVoice) {
      errors.alertType = "Please choose an message type.";
      errors.tab0 = true;
    }

    if (
      !alert?.allGroups &&
      (!Array.isArray(alert.groups) || alert.groups.length === 0) &&
      (!alert?.users || alert?.users.length === 0)
    ) {
      errors.groups = "Please pick a group or user to receive your message.";
      errors.tab1 = true;
    }

    // Message tab
    if (!alert?.includeText && !alert.includeEmail && !alert.includeVoice)
      errors.tab2 = true;

    if (!alert.name) {
      errors.name = "A message name is required.";
      errors.tab2 = true;
    }

    if (isMultiType(alert)) {
      if (!alert?.smsMessage) {
        errors.smsMessage = "A text message is required.";
        errors.tab2 = true;
      }
    } else {
      if (alert?.includeText && !alert?.smsMessage) {
        errors.smsMessage = "A text message is required.";
        errors.tab2 = true;
      }
      if (alert.includeEmail && !alert.emailMessage) {
        errors.emailMessage = "An email body is required.";
        errors.tab2 = true;
      }
      if (alert.includeVoice) {
        if (
          state.voiceTab === "record" &&
          !alert.voiceRecording &&
          !alert.voiceFileIdentifier
        ) {
          errors.voiceRecording = "A voice recording is required.";
          errors.tab2 = true;
        }
        if (state.voiceTab !== "record" && !alert.t2sUrl) {
          errors.voiceRecording = "A recording is required.";
          errors.tab2 = true;
        }
      }
      if (
        alert.includeVoice &&
        state.voiceTab !== "record" &&
        state.ttsDurationSeconds > MAX_TTS_DURATION_SECONDS
      ) {
        errors.voiceRecording =
          "Voice message exceeds the 2 minute limit. Please shorten your message and convert again.";
        errors.tab2 = true;
      }
    }

    if (
      isMultiType(alert) &&
      alert?.includeVoice &&
      state.ttsDurationSeconds > MAX_TTS_DURATION_SECONDS
    ) {
      errors.voiceRecording =
        "Voice message exceeds the 2 minute limit. Please shorten your message.";
      errors.tab2 = true;
    }

    return errors;
  }

  async function handleMultiTypeNext() {
    if (!messageTabValid) return;

    // Only run TTS conversion if needed
    if (
      isMultiType(alert) &&
      alert?.smsMessage &&
      (alert?.t2sMessage !== alert?.smsMessage || !alert?.t2sUrl)
    ) {
      updateState({ convertingTTS: true, ttsDurationSeconds: null });
      const result = await actions.parishSoftApis.textToSpeech(
        alert.smsMessage,
      );
      const durationSeconds = result?.success
        ? await getAudioDurationSeconds(result?.fileUrl)
        : null;
      updateState({
        convertingTTS: false,
        lastTTSMessage: alert?.smsMessage,
        ttsDurationSeconds: durationSeconds,
      });
      if (result.success) {
        dispatch("UPDATE_NEW_ALERT", {
          ...alert,
          t2sMessage: alert?.smsMessage,
          t2sUrl: result?.fileUrl,
          voiceFileIdentifier: result?.fileIdentifier,
        });
      }

      if (durationSeconds > MAX_TTS_DURATION_SECONDS) return;
    }

    changeStep(3);
  }

  return (
    <>
      <ValidationContext.Provider value={validation}>
        <div className="send-alert">
          <h2>
            Send Message
            {isMobile && (
              <i
                className="fal fa-times"
                onClick={() =>
                  howTabValid ? showPopup("confirmClose") : navigate("/")
                }
              ></i>
            )}
          </h2>

          <div className="work-area">
            {/* Alert Type */}
            <ExpandPanel
              className="expand-tile"
              title="How would you like to send it?"
              expanded={uiStep === 0}
              complete={howTabValid}
              onHeaderClick={() => changeStep(0)}
              hasError={validation.getDisplayError("tab0")}
            >
              <div
                className={
                  "alert-type" + (alert?.isEmergency ? " emergency" : "")
                }
              >
                <span
                  className={alert?.includeText ? "selected" : ""}
                  onClick={(e) => toggleType("includeText")}
                >
                  <i className="fal fa-comment-alt-lines"></i>
                  <span id="text" className="alert-type-text">
                    Text
                  </span>
                </span>
                <span
                  className={alert?.includeEmail ? "selected" : ""}
                  onClick={(e) => toggleType("includeEmail")}
                >
                  <i className="fal fa-envelope"></i>
                  <span id="email" className="alert-type-text">
                    Email
                  </span>
                </span>
                <span
                  className={alert?.includeVoice ? "selected" : ""}
                  onClick={(e) => toggleType("includeVoice")}
                >
                  <i className="fal fa-microphone"></i>
                  <span id="voice" className="alert-type-text">
                    Voice
                  </span>
                </span>
              </div>

              <Error name="alertType" />
              <div className="next-button-container">
                <GuiButton
                  dataQa="step1-next"
                  disabled={!howTabValid}
                  onClick={(e) => onHowPanelHeaderClick()}
                >
                  Next
                </GuiButton>
              </div>
            </ExpandPanel>

            {/* Recipients */}
            <ExpandPanel
              className="expand-tile"
              title="Who do you want to send it to?"
              expanded={uiStep === 1}
              complete={whoTabValid}
              onHeaderClick={() => {
                if (!state.savingFamilyMembers) changeStep(1);
              }}
              hasError={validation.getDisplayError("tab1")}
            >
              {!alert.allGroups && (
                <div className="recipient-list">
                  <label>Recipients:</label>
                  {Array.isArray(alert?.groups) &&
                    alert.groups.map(
                      (g) =>
                        !g.is_temp && (
                          <ListItem onRemove={(e) => removeGroup(g)} key={g.id}>
                            {g.name}
                          </ListItem>
                        ),
                    )}
                  <div className="recipient-actions">
                    {(() => {
                      const hasSelection = (alert?.groups?.length ?? 0) > 0;
                      const activePickerType = hasSelection
                        ? state.activePickerType ||
                          alert?.groups?.[0]?._pickerType ||
                          null
                        : null;
                      return (
                        <>
                          <button
                            id="family-btn"
                            className="btn btn-outline"
                            onClick={() => {
                              updateState({ activePickerType: "family" });
                              showPopup("pickGroups");
                            }}
                            disabled={
                              hasSelection && activePickerType !== "family"
                            }
                          >
                            <i className="fas fa-plus"></i>Family
                          </button>
                          <button
                            id="member-btn"
                            className="btn btn-outline"
                            onClick={() => {
                              updateState({ activePickerType: "member" });
                              showPopup("pickMembers");
                            }}
                            disabled={
                              hasSelection && activePickerType !== "member"
                            }
                          >
                            <i className="fas fa-plus"></i>Member
                          </button>
                          <button
                            id="member-workgroup-btn"
                            className="btn btn-outline"
                            onClick={() => {
                              updateState({
                                activePickerType: "memberWorkgroup",
                              });
                              showPopup("pickMemberWorkgroups");
                            }}
                            disabled={
                              hasSelection &&
                              activePickerType !== "memberWorkgroup"
                            }
                          >
                            <i className="fas fa-plus"></i>Member Workgroup
                          </button>
                          <button
                            id="family-workgroup-btn"
                            className="btn btn-outline"
                            onClick={() => {
                              updateState({
                                activePickerType: "familyWorkgroup",
                              });
                              showPopup("pickFamilyWorkgroups");
                            }}
                            disabled={
                              hasSelection &&
                              activePickerType !== "familyWorkgroup"
                            }
                          >
                            <i className="fas fa-plus"></i>Family Workgroup
                          </button>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
              <Error name="groups" />

              {recipientContactNotices.length > 0 && (
                <div className="recipient-contact-notice">
                  <div className="recipient-contact-notice__title">
                    <i className="fas fa-exclamation-triangle"></i>
                    Some recipients may not receive this message:
                  </div>
                  <ul className="recipient-contact-notice__list">
                    {recipientContactNotices.map((notice, idx) => (
                      <li key={idx}>{notice}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="next-button-container">
                <GuiButton
                  dataQa="step2-done"
                  disabled={!whoTabValid || state.savingFamilyMembers}
                  onClick={(e) => changeStep(2)}
                >
                  {state.savingFamilyMembers ? <Spinner /> : "Next"}
                </GuiButton>
              </div>
            </ExpandPanel>

            {/* Create Message */}
            <ExpandPanel
              className="expand-tile"
              title="Create your message"
              expanded={uiStep === 2}
              complete={messageTabValid}
              onHeaderClick={() => {
                if (!state.savingFamilyMembers) changeStep(2);
              }}
              hasError={validation.getDisplayError("tab2")}
            >
              {alert?.includeText && (
                <>
                  {/* 2-way text dropdown */}
                  <div className="txt-recipients-response-label">
                    <label htmlFor="ddl2WayText">
                      Text Recipient Response:
                    </label>
                  </div>
                  <div className="txt-recipients-response">
                    <GuiSelect
                      name="ddl2WayText"
                      value={requestResponse}
                      options={[
                        {
                          value: "1",
                          label: "Request recipient message response",
                        },
                        {
                          value: "0",
                          label: "No response requested from recipient",
                        },
                      ]}
                      onChange={(name, item) => setRequestResponse(item.value)}
                      placeholder="Select response option"
                    />
                  </div>
                </>
              )}
              {alert?.includeEmail && (
                <div className="sender-reply-row">
                  <div className="sender-reply-field sender-reply-field--sender">
                    <div className="sender-reply-input-border">
                      <div className="sender-reply-input-inner">
                        <input
                          className="sender-reply-input"
                          placeholder="Sender Name"
                          value={alert.emailSenderName || ""}
                          onChange={(e) =>
                            update("emailSenderName", e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="sender-reply-label-chip">
                      <span className="sender-reply-label-text">
                        Sender Name
                      </span>
                    </div>
                  </div>

                  <div className="sender-reply-field sender-reply-field--reply">
                    <div className="sender-reply-select-border sender-reply-select-border-embed">
                      <GuiSelect
                        name="replyTo"
                        value={requestEmailResponse || ""}
                        options={emailDataSender.map((e) => ({
                          value: e.email?.toString() || "",
                          label: e.email || "",
                        }))}
                        onChange={(name, item) =>
                          setRequestEmailResponse(item.value)
                        }
                        placeholder="Select"
                      />
                    </div>
                    <div
                      className="sender-reply-label-chip"
                      style={{ top: -9 }}
                    >
                      <span className="sender-reply-label-text">
                        Reply-To Email
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div
                className={
                  alert?.includeEmail
                    ? "subject-wrapper subject-wrapper--email"
                    : "subject-wrapper"
                }
              >
                <GuiTextbox
                  dataQa="subject"
                  className="subject"
                  label="Subject"
                  name="name"
                  value={alert.name}
                  onChange={(name, value) => {
                    dispatch("UPDATE_NEW_ALERT", {
                      ...alert,
                      name: value,
                      emailSubject: value,
                    });
                    updateState({ previewed: false });
                  }}
                  maxLength={100}
                />
              </div>

              {/* If multi-type, show only Text message field and sync to others */}
              {isMultiType(alert) ? (
                <>
                  <div className="sms-message-header">
                    <span className="sms-message-label">Message</span>
                  </div>
                  {!isMobile && (
                    <SmsCreditsIndicator
                      charCount={alert?.smsMessage?.length || 0}
                    />
                  )}
                  <GuiTextArea
                    dataQa="text-subject"
                    name="smsMessage"
                    value={alert?.smsMessage}
                    maxLength={SMS_CHAR_LIMIT}
                    onChange={handleSmsChangeMulti}
                    onPaste={handleSmsPasteCapped}
                    onKeyDown={handleSmsKeyDown}
                  />
                  {isMobile && (
                    <SmsCreditsIndicator
                      charCount={alert?.smsMessage?.length || 0}
                    />
                  )}
                  {renderSpamWarnings("text")}
                  {(!alert.name?.trim() || !alert.smsMessage?.trim()) && (
                    <div className="error-class">
                      You must fill in both the subject and message fields to
                      proceed.
                    </div>
                  )}
                  {state.ttsDurationSeconds > 0 && (
                    <div className="tts-measured-duration">
                      Converted length:{" "}
                      {formatDurationShort(state.ttsDurationSeconds)}
                    </div>
                  )}
                  {state.ttsTrim?.overflowText && (
                    <div className="tts-trim-preview">
                      <span className="tts-trim-kept">
                        {state.ttsTrim.keptText}
                      </span>{" "}
                      <span
                        className="tts-trim-overflow"
                        title="Not included in the voice recording — exceeds the 2 minute limit"
                      >
                        {state.ttsTrim.overflowText}
                      </span>
                    </div>
                  )}
                  {state.ttsDurationSeconds > MAX_TTS_DURATION_SECONDS &&
                    !state.ttsBannerDismissed && <TtsDurationBanner />}

                  <div className="next-button-container">
                    <GuiButton
                      dataQa="use-recording"
                      disabled={!messageTabValid || state.convertingTTS}
                      onClick={handleMultiTypeNext}
                    >
                      {state.convertingTTS ? <Spinner /> : "Next"}
                    </GuiButton>
                  </div>
                </>
              ) : (
                <>
                  {/* Existing logic for single type selection */}
                  {alert?.includeText && (
                    <>
                      <div className="sms-message-header">
                        <span className="sms-message-label">Message</span>
                      </div>
                      {!isMobile && (
                        <SmsCreditsIndicator
                          charCount={alert?.smsMessage?.length || 0}
                        />
                      )}
                      <GuiTextArea
                        dataQa="text-subject"
                        name="smsMessage"
                        value={alert?.smsMessage}
                        maxLength={SMS_CHAR_LIMIT}
                        onChange={handleSmsChangeSingle}
                        onPaste={handleSmsPasteCapped}
                        onKeyDown={handleSmsKeyDown}
                        onDrop={(e) => e.preventDefault()} // Prevent drag-drop paste
                      />
                      {isMobile && (
                        <SmsCreditsIndicator
                          charCount={alert?.smsMessage?.length || 0}
                        />
                      )}
                      {renderSpamWarnings("text")}
                      {(!alert.name?.trim() || !alert.smsMessage?.trim()) && (
                        <div className="error-class">
                          You must fill in both the subject and message fields
                          to proceed.
                        </div>
                      )}
                    </>
                  )}

                  {alert.includeEmail && (
                    <>
                      <GuiHtmlEditor
                        id="email-body"
                        label="Message"
                        name="emailMessage"
                        value={alert.emailMessage}
                        onChange={update}
                        onAttachmentAdded={onAttachmentAdded}
                      />
                      <div className="attachments">
                        {alert.attachments.map((a) => (
                          <div className="attachment" key={a.url}>
                            <i className="icon far fa-paperclip"></i>
                            {a.name}
                            <i
                              className="close fal fa-times"
                              onClick={(e) => removeAttachment(a)}
                            ></i>
                          </div>
                        ))}
                      </div>
                      {renderSpamWarnings("email")}
                    </>
                  )}

                  {alert.includeVoice && (
                    <>
                      <div className="voice-text-toggle">
                        <div id="voice-recording" className="voice-recording">
                          <TabItem
                            tab="record"
                            currentTab={state.voiceTab}
                            onClick={(e) => updateState({ voiceTab: "record" })}
                          >
                            Voice Recording
                          </TabItem>
                        </div>
                        <div id="text-to-voice" className="text-to-voice">
                          <TabItem
                            tab="texttospeech"
                            currentTab={state?.voiceTab}
                            onClick={(e) =>
                              updateState({ voiceTab: "texttospeech" })
                            }
                          >
                            Text to Voice
                          </TabItem>
                        </div>
                        {/* Tab for future Callback option */}
                        {/*<div className="text-to-voice"><TablItem tab="callback" currentTab={state.voiceTab} onClick={e => setVoiceTab("callback")}>Callback</TablItem></div>*/}
                      </div>

                      {state?.voiceTab === "record" && (
                        <VoiceRecorder
                          name="voiceRecording"
                          value={alert?.voiceRecording || alert?.voiceFileUrl}
                          onChange={updateVoiceRecording}
                          error={validation.getDisplayError("voiceRecording")}
                          autoPlay={true}
                          onError={(msg) => showToast("error", msg)}
                        />
                      )}

                      {state?.voiceTab === "texttospeech" && (
                        <div className="text-to-speech">
                          <GuiTextArea
                            dataQa="body"
                            label={
                              "Message" +
                              (state.lastTTSMessage &&
                              state.lastTTSMessage !== alert.t2sMessage
                                ? " *"
                                : "")
                            }
                            name="t2sMessage"
                            value={alert.t2sMessage}
                            maxLength={TTS_CHAR_LIMIT}
                            onChange={handleTtsMessageChange}
                            onAttachmentAdded={onAttachmentAdded}
                          />
                          <TtsCharCounter
                            charCount={alert?.t2sMessage?.length || 0}
                          />

                          {renderSpamWarnings("voice")}

                          {!alert?.t2sUrl && (
                            <div className="next-button-container">
                              <GuiButton
                                dataQa="convert"
                                disabled={!alert?.t2sMessage}
                                onClick={convertTTS}
                              >
                                {state.convertingTTS ? (
                                  <Spinner />
                                ) : (
                                  "Convert to Voice"
                                )}
                              </GuiButton>
                            </div>
                          )}
                          {alert?.t2sUrl && (
                            <>
                              <div className="player">
                                <AudioPlayer
                                  src={alert?.t2sUrl}
                                  autoPlay={true}
                                />
                              </div>
                              {state.ttsDurationSeconds > 0 && (
                                <div className="tts-measured-duration">
                                  Converted length:{" "}
                                  {formatDurationShort(
                                    state.ttsDurationSeconds,
                                  )}
                                </div>
                              )}
                              {state.ttsTrim?.overflowText && (
                                <div className="tts-trim-preview">
                                  <span className="tts-trim-kept">
                                    {state.ttsTrim.keptText}
                                  </span>{" "}
                                  <span
                                    className="tts-trim-overflow"
                                    title="Not included in the voice recording — exceeds the 2 minute limit"
                                  >
                                    {state.ttsTrim.overflowText}
                                  </span>
                                </div>
                              )}
                              {state.ttsDurationSeconds >
                                MAX_TTS_DURATION_SECONDS &&
                                !state.ttsBannerDismissed && (
                                  <TtsDurationBanner />
                                )}
                              <div className="next-button-container">
                                <GuiButton
                                  dataQa="update"
                                  className="btn-outline update-tts"
                                  disabled={
                                    !alert?.t2sMessage ||
                                    state.lastTTSMessage === alert?.t2sMessage
                                  }
                                  onClick={convertTTS}
                                >
                                  {state.convertingTTS ? <Spinner /> : "Update"}
                                </GuiButton>
                                <GuiButton
                                  dataQa="use-recording"
                                  disabled={!messageTabValid}
                                  onClick={(e) => changeStep(3)}
                                >
                                  Use Recording
                                </GuiButton>
                              </div>
                              {!messageTabValid && (
                                <div className="tts-blocking-reason">
                                  {validation.getError("name") ||
                                    validation.getError("voiceRecording") ||
                                    "Please complete the required fields to continue."}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {(!alert?.includeVoice ||
                    state.voiceTab !== "texttospeech") && (
                    <div className="next-button-container">
                      <GuiButton
                        dataQa="use-recording"
                        disabled={!messageTabValid}
                        onClick={(e) => changeStep(3)}
                      >
                        {alert?.includeVoice ? "Use Recording" : "Next"}
                      </GuiButton>
                    </div>
                  )}
                </>
              )}
            </ExpandPanel>

            <ExpandPanel
              title="Preview and Send"
              className="preview expand-tile"
              expanded={uiStep === 3}
              complete={state?.previewed}
              onHeaderClick={() => {
                if (!state.convertingTTS) changeStep(3);
              }}
              disabled={state.convertingTTS}
            >
              {/* Selected Types with Green Check */}
              <div className="check-icon-class">
                {alert?.includeText && (
                  <span className="green-check">
                    <i className="fas fa-check green-check-icon"></i> Text
                  </span>
                )}
                {alert?.includeEmail && (
                  <span className="green-check">
                    <i className="fas fa-check green-check-icon"></i> Email
                  </span>
                )}
                {alert?.includeVoice && (
                  <span className="green-check">
                    <i className="fas fa-check green-check-icon"></i> Voice
                  </span>
                )}
              </div>
              <label>Message Title</label>
              <div>{alert?.name}</div>
              {alert?.isScheduled && (
                <>
                  <label>Scheduled For</label>
                  <div>{formatGridDate(alert?.scheduledDate)}</div>
                </>
              )}
              <label>Recipients</label>
              <div>
                {alert.allGroups
                  ? "All Groups"
                  : Array.isArray(alert.groups)
                    ? alert.groups.map((r) => r.name).join(", ")
                    : ""}
              </div>
              {alert?.includeText && (
                <>
                  <label>Preview Text Message</label>
                  <div className="break-word">{alert?.smsMessage}</div>
                </>
              )}
              {alert?.includeEmail && (
                <>
                  <label>Preview Email</label>
                  <div className="box-card">
                    <div className="box-subject">
                      <label>Subject:</label> {alert?.name}
                    </div>
                    <div
                      className="box-body break-word"
                      dangerouslySetInnerHTML={{ __html: sanitizedEmailHTML }}
                    ></div>
                  </div>
                </>
              )}
              {alert?.includeVoice && (
                <>
                  <label>Voice Message</label>
                  {state?.convertingTTS ? (
                    <Spinner />
                  ) : alert?.voiceRecording ? (
                    <AudioPlayer src={alert?.voiceRecording} autoPlay={false} />
                  ) : alert?.t2sUrl ? (
                    <AudioPlayer src={alert?.t2sUrl} autoPlay={false} />
                  ) : (
                    <div className="voice-preview-error">
                      Voice preview will appear after conversion or recording.
                    </div>
                  )}
                  {alert?.t2sUrl && state.ttsDurationSeconds > 0 && (
                    <div className="tts-measured-duration">
                      Converted length:{" "}
                      {formatDurationShort(state.ttsDurationSeconds)}
                    </div>
                  )}
                  {alert?.t2sUrl &&
                    state.ttsDurationSeconds > MAX_TTS_DURATION_SECONDS &&
                    !state.ttsBannerDismissed && <TtsDurationBanner />}
                </>
              )}
            </ExpandPanel>
          </div>

          {/* Buttons */}
          {isMobile && !alert?.isEmergency ? (
            <div className="send-alert-bottom-button-bar-mobile">
              {!hasSpamContent && (
                <>
                  <GuiButton
                    dataQa="schedule"
                    className="btn-outline schedule"
                    disabled={!state?.previewed || !messageTabValid}
                    onClick={scheduleClick}
                  >
                    Schedule
                  </GuiButton>
                  <GuiButton
                    dataQa="send"
                    className="send"
                    disabled={!state?.previewed || !messageTabValid}
                    onClick={save}
                  >
                    Send
                  </GuiButton>
                </>
              )}
            </div>
          ) : (
            <div className="send-alert-bottom-button-bar">
              <div className="bottom-button-bar-discard-draft">
                {!isMobile && (
                  <>
                    <GuiButton
                      dataQa="discard"
                      className="btn-outline"
                      onClick={(e) => {
                        e.preventDefault();
                        showPopup("confirmDiscard");
                      }}
                    >
                      Discard
                    </GuiButton>
                  </>
                )}
                <GuiButton
                  dataQa="save-draft"
                  className="btn-outline save-as"
                  disabled={!howTabValid}
                  onClick={(e) => howTabValid && save(true)}
                >
                  Save Draft
                </GuiButton>
              </div>
              <div className="bottom-button-bar-schedule-send">
                {!alert.isEmergency && !hasSpamContent && (
                  <GuiButton
                    dataQa="schedule"
                    className="btn-outline"
                    disabled={!state?.previewed || !messageTabValid}
                    onClick={scheduleClick}
                  >
                    Schedule
                  </GuiButton>
                )}
                {!hasSpamContent && (
                  <GuiButton
                    dataQa="send"
                    className="send"
                    disabled={!state?.previewed || !messageTabValid}
                    onClick={save}
                  >
                    Send
                  </GuiButton>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modals and Popups */}
        <LoadingSpinner show={saving} className="loading-page" />

        <FamilyPicker
          show={pickGroups}
          selected={alert?.groups}
          onDone={groupsChanged}
          onBack={hidePopup}
        />
        <MemberPicker
          show={pickMembers}
          selected={alert?.groups}
          onDone={groupsChanged}
          onBack={hidePopup}
        />
        <FamilyWorkgroupPicker
          show={pickFamilyWorkgroups}
          selected={alert?.groups}
          onDone={groupsChanged}
          onBack={hidePopup}
        />
        <MemberWorkgroupPicker
          show={pickMemberWorkgroups}
          selected={alert?.groups}
          onDone={groupsChanged}
          onBack={hidePopup}
        />
        {confirmSave && (
          <ScheduleAlertModal
            value={alert?.scheduledDate}
            onSave={saveSchedule}
            onCancel={hidePopup}
          />
        )}
        {confirmClose && (
          <ConfirmCloseModal
            onDelete={discardAlert}
            onSaveDraft={(e) => save(true)}
            onCancel={hidePopup}
          />
        )}
        {confirmDiscard && (
          <ConfirmDeleteModal
            status="discard"
            onDelete={discardAlert}
            onCancel={hidePopup}
          />
        )}
      </ValidationContext.Provider>

      {/* Delete modal removed: unused in embed flow */}
    </>
  );
}
